import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { IoCloudDownloadOutline, IoCloudUploadOutline } from 'react-icons/io5';

import {
  Notice,
  Section,
  errorText,
  formatDate,
  input,
  mono,
  primary,
  secondary,
} from './PipelineSettingsPanel';
import { confirmDialog } from '../../components/ConfirmDialog';
import { configApi } from '../../services/api';

// Configuration → Approvals and → Transfer.
// Approvals: changes to sensitive settings waiting for a second admin (only
// when the deployment sets REQUIRE_SECOND_APPROVAL). Transfer: export this
// environment's configuration as a file, and import one with a preview of
// exactly what it would change.

const fail = (error) => toast.error(errorText(error));

const shown = (value) => (value === null ? 'default' : String(value));

export const Approvals = ({ token, canManage }) => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setRows((await configApi.requests(token)).requests || []);
  }, [token]);

  useEffect(() => {
    load().catch(fail);
  }, [load]);

  const decide = async (row, approve) => {
    const ok = await confirmDialog({
      title: approve ? 'Approve and publish?' : 'Reject this request?',
      message: approve
        ? `Publishes ${Object.keys(row.changes).join(', ')} as requested by ${row.requested_by}.`
        : 'The change is not published. The requester can ask again.',
      confirmLabel: approve ? 'Approve' : 'Reject',
      danger: !approve,
    });
    if (!ok) return;
    setBusy(true);
    try {
      if (approve) await configApi.approve(token, row._id);
      else await configApi.reject(token, row._id, null);
      await load();
      toast.success(approve ? 'Approved and published' : 'Rejected');
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  if (!rows) return <p className="text-sm text-zinc-400">Loading…</p>;

  return (
    <Section
      title="Waiting for approval"
      hint="Changes to sensitive settings need a second admin. You cannot approve your own; rejecting your own withdraws it."
    >
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-400">
          Nothing is waiting. Requests appear here only when this deployment
          requires a second approval (REQUIRE_SECOND_APPROVAL) and someone
          changes a sensitive setting.
        </p>
      ) : (
        <ul className="divide-y divide-zinc-800">
          {rows.map((row) => (
            <li
              key={row._id}
              className="flex flex-wrap items-start justify-between gap-3 py-3"
            >
              <div className="min-w-0 text-sm">
                <div className={`${mono} text-zinc-200`}>
                  {Object.entries(row.changes)
                    .map(([k, v]) => `${k} → ${shown(v)}`)
                    .join(', ')}
                </div>
                <div className="text-zinc-300">{row.rationale}</div>
                <div className="text-xs text-zinc-400">
                  {row.requested_by} · {formatDate(row.requested_at)}
                  {row.effective_from
                    ? ` · takes effect ${formatDate(row.effective_from)}`
                    : ''}
                </div>
              </div>
              {canManage && (
                <div className="flex gap-2">
                  <button
                    className={primary}
                    disabled={busy}
                    onClick={() => decide(row, true)}
                  >
                    Approve
                  </button>
                  <button
                    className={secondary}
                    disabled={busy}
                    onClick={() => decide(row, false)}
                  >
                    Reject
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
};

const PlanList = ({ plan }) => (
  <div className="space-y-2 text-sm">
    {plan.changes === 0 && (
      <p className="text-emerald-400">
        Nothing to change: this environment already matches the file.
      </p>
    )}
    {Object.entries(plan.settings).map(([key, { from, to }]) => (
      <div key={key} className={`${mono} text-zinc-200`}>
        setting {key}: {shown(from ?? null)} → {shown(to)}
      </div>
    ))}
    {plan.prompts.map((key) => (
      <div key={key} className={`${mono} text-zinc-200`}>
        prompt {key}: new version
      </div>
    ))}
    {plan.rubrics.map((key) => (
      <div key={key} className={`${mono} text-zinc-200`}>
        rubric {key}: new version
      </div>
    ))}
    {plan.admission && (
      <div className={`${mono} text-zinc-200`}>
        admission policy: threshold {plan.admission.to.threshold}, quota{' '}
        {plan.admission.to.quota_cutoff}
      </div>
    )}
    {plan.unknown.length > 0 && (
      <p className="text-amber-300">
        Skipped, not known here: {plan.unknown.join(', ')}
      </p>
    )}
  </div>
);

export const Transfer = ({ token, canManage }) => {
  const [bundle, setBundle] = useState(null);
  const [plan, setPlan] = useState(null);
  const [report, setReport] = useState(null);
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  const download = async () => {
    try {
      const data = await configApi.exportBundle(token);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `oqlous-config-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      fail(error);
    }
  };

  const choose = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPlan(null);
    setReport(null);
    try {
      const parsed = JSON.parse(await file.text());
      const result = await configApi.importBundle(token, parsed);
      setBundle(parsed);
      setPlan(result.plan);
    } catch (error) {
      fail(error instanceof SyntaxError ? 'That file is not JSON.' : error);
    }
  };

  const apply = async () => {
    const ok = await confirmDialog({
      title: `Apply ${plan.changes} change(s)?`,
      message:
        'Each one is published as a new version through the normal checks; sensitive settings may need a second admin.',
      confirmLabel: 'Apply',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const result = await configApi.importBundle(
        token,
        bundle,
        true,
        rationale,
      );
      setReport(result);
      setPlan(null);
      setRationale('');
      toast.success('Imported');
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Section
        title="Export"
        hint="Settings that differ from code, published prompts and rubrics, and the admission policy, as one JSON file."
      >
        <button className={secondary} onClick={download}>
          <IoCloudDownloadOutline aria-hidden /> Download configuration
        </button>
      </Section>
      {canManage && (
        <Section
          title="Import"
          hint="Shows what a file would change before anything is applied. Nothing is overwritten in place and nothing missing from the file is removed."
        >
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={choose}
          />
          <button
            className={secondary}
            onClick={() => fileInput.current?.click()}
          >
            <IoCloudUploadOutline aria-hidden /> Choose a file
          </button>
          {plan && (
            <div className="mt-4 space-y-3">
              <PlanList plan={plan} />
              {plan.changes > 0 && (
                <>
                  <input
                    className={input}
                    placeholder="Why are you importing this? (required, kept in history)"
                    value={rationale}
                    onChange={(e) => setRationale(e.target.value)}
                  />
                  <button
                    className={primary}
                    disabled={busy || !rationale.trim()}
                    onClick={apply}
                  >
                    Apply {plan.changes} change(s)
                  </button>
                </>
              )}
            </div>
          )}
          {report && (
            <div className="mt-4 space-y-2 text-sm">
              {report.done.map((d) => (
                <div key={d.item} className="text-emerald-400">
                  {d.item}: {JSON.stringify(d.result)}
                </div>
              ))}
              {report.failed.length > 0 && (
                <Notice>
                  {report.failed
                    .map((f) => `${f.item}: ${f.error}`)
                    .join(' · ')}
                </Notice>
              )}
            </div>
          )}
        </Section>
      )}
    </div>
  );
};
